import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { guestPortalApi } from '../../lib/api';
import { useGuestAuth } from '../../lib/guest-auth-context';
import { format } from 'date-fns';
import {
  Hotel,
  Calendar,
  Users,
  CreditCard,
  Check,
  ArrowLeft,
  Loader2,
  MapPin,
  Clock,
} from 'lucide-react';
import { Card, CardContent, CardDescription, CardHeader, CardTitle, CardFooter } from '../ui/card';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Textarea } from '../ui/textarea';
import { Separator } from '../ui/separator';
import { toast } from 'sonner';

export default function GuestBookingPage() {
  const navigate = useNavigate();
  const { token, guest, isAuthenticated, isLoading: authLoading } = useGuestAuth();
  const [loading, setLoading] = useState(false);
  const [booking, setBooking] = useState(null);
  const [specialRequests, setSpecialRequests] = useState('');

  useEffect(() => {
    const stored = sessionStorage.getItem('bookingSelection');
    if (!stored) {
      toast.error('No booking selection found');
      navigate('/portal');
      return;
    }
    setBooking(JSON.parse(stored));
  }, [navigate]);

  useEffect(() => {
    // Wait for auth to finish loading before checking authentication
    if (authLoading) return;
    
    if (!isAuthenticated && booking) {
      // Not logged in, redirect to login
      toast.info('Please sign in to complete your booking');
      navigate('/portal/login');
    }
  }, [isAuthenticated, authLoading, booking, navigate]);

  const handleConfirmBooking = async () => {
    if (!isAuthenticated) {
      navigate('/portal/login');
      return;
    }

    try {
      setLoading(true);
      
      const response = await guestPortalApi.createReservation(token, {
        property_id: booking.property_id,
        room_type_id: booking.room_type.id,
        rate_plan_id: booking.rate_plan?.id,
        arrival_date: booking.arrival_date,
        departure_date: booking.departure_date,
        adults: booking.adults,
        children: booking.children,
        special_requests: specialRequests || null,
      });

      // Clear booking selection
      sessionStorage.removeItem('bookingSelection');
      
      toast.success('Booking confirmed!');
      
      // Store confirmation for display
      sessionStorage.setItem('bookingConfirmation', JSON.stringify({
        ...response.data,
        property: booking.property,
        room_type: booking.room_type,
        arrival_date: booking.arrival_date,
        departure_date: booking.departure_date,
      }));
      
      navigate('/portal/booking/confirmation');
    } catch (error) {
      console.error('Booking failed:', error);
      toast.error(error.response?.data?.detail || 'Failed to complete booking');
    } finally {
      setLoading(false);
    }
  };

  if (!booking) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <Loader2 className="h-8 w-8 animate-spin text-primary" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-b from-background to-muted/30" data-testid="guest-booking-page">
      {/* Header */}
      <header className="border-b bg-background/95 backdrop-blur sticky top-0 z-50">
        <div className="container mx-auto px-4 py-4">
          <Button variant="ghost" onClick={() => navigate('/portal')} className="gap-2">
            <ArrowLeft className="h-4 w-4" />
            Back to Search
          </Button>
        </div>
      </header>

      <main className="container mx-auto px-4 py-8">
        <div className="max-w-4xl mx-auto">
          <h1 className="text-3xl font-bold mb-8">Complete Your Booking</h1>

          <div className="grid grid-cols-1 lg:grid-cols-3 gap-8">
            {/* Booking Form */}
            <div className="lg:col-span-2 space-y-6">
              {/* Guest Info */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Guest Information</CardTitle>
                  <CardDescription>Your details for this reservation</CardDescription>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div className="grid grid-cols-2 gap-4">
                    <div className="space-y-2">
                      <Label>First Name</Label>
                      <Input value={guest?.first_name || ''} disabled />
                    </div>
                    <div className="space-y-2">
                      <Label>Last Name</Label>
                      <Input value={guest?.last_name || ''} disabled />
                    </div>
                  </div>
                  <div className="space-y-2">
                    <Label>Email</Label>
                    <Input value={guest?.email || ''} disabled />
                  </div>
                </CardContent>
              </Card>

              {/* Room Details */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Room Details</CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="flex gap-4">
                    <div className="w-24 h-24 bg-muted rounded-lg flex items-center justify-center shrink-0">
                      <Hotel className="h-10 w-10 text-muted-foreground" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-lg">{booking.room_type?.name}</h3>
                      <p className="text-muted-foreground text-sm">{booking.room_type?.description}</p>
                      <p className="text-sm mt-2">
                        <span className="text-muted-foreground">Max occupancy:</span> {booking.room_type?.max_occupancy} guests
                      </p>
                      <p className="text-sm">
                        <span className="text-muted-foreground">Rate plan:</span> {booking.rate_plan?.name}
                      </p>
                    </div>
                  </div>
                </CardContent>
              </Card>

              {/* Special Requests */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Special Requests</CardTitle>
                  <CardDescription>Any special requests for your stay (optional)</CardDescription>
                </CardHeader>
                <CardContent>
                  <Textarea
                    placeholder="E.g., high floor, early check-in, dietary requirements..."
                    value={specialRequests}
                    onChange={(e) => setSpecialRequests(e.target.value)}
                    rows={4}
                    data-testid="special-requests-input"
                  />
                </CardContent>
              </Card>

              {/* Policies */}
              <Card>
                <CardHeader>
                  <CardTitle className="text-lg">Policies</CardTitle>
                </CardHeader>
                <CardContent className="space-y-3 text-sm">
                  <div className="flex items-start gap-2">
                    <Clock className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    <p><strong>Check-in:</strong> From 2:00 PM · <strong>Check-out:</strong> Until 11:00 AM</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <CreditCard className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    <p><strong>Payment:</strong> Payment will be collected at the property</p>
                  </div>
                  <div className="flex items-start gap-2">
                    <Check className="h-4 w-4 mt-0.5 text-muted-foreground shrink-0" />
                    <p><strong>Cancellation:</strong> Free cancellation up to 24 hours before arrival</p>
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Booking Summary */}
            <div>
              <Card className="sticky top-24">
                <CardHeader>
                  <CardTitle className="text-lg">Booking Summary</CardTitle>
                </CardHeader>
                <CardContent className="space-y-4">
                  <div>
                    <h3 className="font-semibold">{booking.property?.name}</h3>
                    <p className="text-sm text-muted-foreground flex items-center gap-1">
                      <MapPin className="h-4 w-4" />
                      {booking.property?.address_city}
                    </p>
                  </div>

                  <Separator />

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Check-in</span>
                      <span className="font-medium">{format(new Date(booking.arrival_date), 'EEE, MMM d, yyyy')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Check-out</span>
                      <span className="font-medium">{format(new Date(booking.departure_date), 'EEE, MMM d, yyyy')}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Duration</span>
                      <span>{booking.nights} night(s)</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Guests</span>
                      <span>{booking.adults} adult(s){booking.children > 0 ? `, ${booking.children} child(ren)` : ''}</span>
                    </div>
                  </div>

                  <Separator />

                  <div className="space-y-2 text-sm">
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">{booking.room_type?.name}</span>
                      <span>${booking.rate_per_night}/night</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Subtotal ({booking.nights} nights)</span>
                      <span>${booking.total_rate?.toFixed(2)}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-muted-foreground">Taxes & fees (15%)</span>
                      <span>${booking.taxes_estimated?.toFixed(2)}</span>
                    </div>
                  </div>

                  <Separator />

                  <div className="flex justify-between text-lg font-bold">
                    <span>Total</span>
                    <span>${booking.grand_total?.toFixed(2)}</span>
                  </div>
                </CardContent>
                <CardFooter>
                  <Button 
                    className="w-full" 
                    size="lg"
                    onClick={handleConfirmBooking}
                    disabled={loading}
                    data-testid="confirm-booking-btn"
                  >
                    {loading ? (
                      <Loader2 className="h-4 w-4 mr-2 animate-spin" />
                    ) : (
                      <Check className="h-4 w-4 mr-2" />
                    )}
                    Confirm Booking
                  </Button>
                </CardFooter>
              </Card>
            </div>
          </div>
        </div>
      </main>
    </div>
  );
}
